import { atom, atomFamily, DefaultValue, selectorFamily, type AtomEffect } from 'recoil';
import type {
  HoldingsBreakdownTypeEnum,
  HoldingsBenchmarkCategory,
  HoldingsCategoryId,
  ImageDetails,
  Scenario,
  TaxRate,
} from 'venn-api';
import type {
  CorrelationLabelDisplayType,
  DataGridSizeType,
  EditorValue,
  GeoLevelFilterType,
  LegendPosition,
  MultiLevelPortfolioFilterSelection,
  PortfolioStrategyFilterType,
  SectorLevelFilterType,
} from 'venn-components';
import {
  type CustomBlockTypeEnum,
  type SelectableCustomNotablePeriod,
  assert,
  isTreeBlock,
  assertExhaustive,
  hasFeatureSelector,
  isHoldingsBreakdownBlock,
} from 'venn-utils';
import { resetOnStudioReset } from '../../effects/signalEffects';
import { blockSettingsMap, holdingsCategoriesTree } from '../globalConfig';
import {
  type BlockId,
  type ScatterAxisConfig,
  type ScatterAxisRange,
  type ScatterAxisRenderedMetadata,
  type ScatterAxisStaticMetadata,
  UnitFormat,
} from '../types';
import { blockSettingId, blockSettings } from './blockSettings';
import { isReportState } from './view';
import { DEFAULT_STOCK_PHOTO } from '../consts';
import { blockLimitedHoldingsSubjectsWithFees } from './blockConfig';

// Safety net for the Recoil store: enforces max >= min on each axis regardless of who writes
// the config. The UI's auto-correct handles the common case (user-aware which field changed);
// this guards against any other writer producing inconsistent state.
const normalizeAxisRange = (range: ScatterAxisRange): ScatterAxisRange =>
  range.min != null && range.max != null && range.min > range.max ? { ...range, max: range.min } : range;

const normalizeScatterAxisConfig = (config: ScatterAxisConfig | undefined): ScatterAxisConfig | undefined => {
  if (!config) {
    return config;
  }

  const x = normalizeAxisRange(config.x);
  const y = normalizeAxisRange(config.y);
  return x === config.x && y === config.y ? config : { x, y };
};

const normalizeScatterAxisConfigEffect: AtomEffect<ScatterAxisConfig | undefined> = ({ onSet, setSelf }) => {
  onSet((newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }

    const normalized = normalizeScatterAxisConfig(newValue);
    if (normalized !== newValue) {
      setSelf(normalized);
    }
  });
};

export const blockScatterAxisConfig = atomFamily<ScatterAxisConfig | undefined, BlockId>({
  key: 'blockScatterAxisConfig',
  default: undefined,
  effects: [normalizeScatterAxisConfigEffect],
});

export const blockScatterAxisStaticHints = atomFamily<ScatterAxisStaticMetadata | undefined, BlockId>({
  key: 'blockScatterAxisStaticHints',
  default: undefined,
});

export const blockScatterAxisRenderedExtremes = atomFamily<ScatterAxisRenderedMetadata | undefined, BlockId>({
  key: 'blockScatterAxisRenderedExtremes',
  default: undefined,
});

export const convertHoldingsBlockTypeToBreakdown = (blockType: CustomBlockTypeEnum): HoldingsBreakdownTypeEnum => {
  assert(isHoldingsBreakdownBlock(blockType));
  switch (blockType) {
    case 'ASSET_EXPOSURE':
      return 'ASSET';
    case 'SECTOR_EXPOSURE':
      return 'SECTOR';
    case 'GEOGRAPHY_EXPOSURE':
      return 'REGION';
    case 'CREDIT_BREAKDOWN':
      return 'CREDIT_QUALITY';
    case 'MATURITY_BREAKDOWN':
      return 'MATURITY';
    case 'DURATION_BREAKDOWN':
      return 'DURATION';
    case 'MUNI_BREAKDOWN':
      return 'US_MUNI_STATE';
    default: {
      return assertExhaustive(blockType);
    }
  }
};

export const blockScenarios = atomFamily<Scenario[], BlockId>({
  key: 'blockScenarios',
  default: [],
});

export const viewSelectedNotablePeriods = atomFamily<number[] | undefined, BlockId>({
  key: 'viewSelectedNotablePeriods',
  default: undefined,
});

export const viewSelectedInvestmentGroupInformation = atomFamily<string[] | undefined, BlockId>({
  key: 'viewSelectedInvestmentGroupInformation',
  default: undefined,
});

export const viewCustomNotablePeriods = atomFamily<SelectableCustomNotablePeriod[] | undefined, BlockId>({
  key: 'viewCustomNotablePeriods',
  default: undefined,
});

export const viewDataGridSizeType = atomFamily<DataGridSizeType | undefined, BlockId>({
  key: 'viewDataGridSizeType',
  default: undefined,
});

export const correlationLabelDisplayType = atomFamily<CorrelationLabelDisplayType | undefined, BlockId>({
  key: 'correlationLabelDisplayType',
  default: undefined,
});

export const portfolioStrategyFilterType = atomFamily<PortfolioStrategyFilterType | undefined, BlockId>({
  key: 'portfolioStrategyFilterType',
  default: undefined,
});

const multiLevelPortfolioStrategyFilterAtom = atomFamily<MultiLevelPortfolioFilterSelection | undefined, BlockId>({
  key: 'multiLevelPortfolioStrategyFilterAtom',
  default: undefined,
});

const migrateLegacyStrategyFilter = (legacy: PortfolioStrategyFilterType): MultiLevelPortfolioFilterSelection => {
  switch (legacy as string) {
    case 'ALL_STRATEGIES':
      return { filterType: 'ALL_STRATEGIES' as PortfolioStrategyFilterType };
    case 'TOP_LEVEL_STRATEGIES':
      return { filterType: 'CUSTOM' as PortfolioStrategyFilterType, levels: [0] };
    case 'DEFAULT':
    case 'CUSTOM':
    default:
      return { filterType: 'DEFAULT' as PortfolioStrategyFilterType };
  }
};

export const pieChartLegendPosition = atomFamily<LegendPosition | undefined, BlockId>({
  key: 'pieChartLegendPosition',
  default: undefined,
});
export const multiLevelPortfolioStrategyFilterType = selectorFamily<
  MultiLevelPortfolioFilterSelection | undefined,
  BlockId
>({
  key: 'multiLevelPortfolioStrategyFilterType',
  get:
    (id) =>
    ({ get }) => {
      const stored = get(multiLevelPortfolioStrategyFilterAtom(id));
      if (stored !== undefined) {
        return stored;
      }
      if (!get(hasFeatureSelector('multilevel_portfolio_filtering_ff'))) {
        return undefined;
      }
      const legacy = get(portfolioStrategyFilterType(id));
      return legacy ? migrateLegacyStrategyFilter(legacy) : undefined;
    },
  set:
    (id) =>
    ({ set }, newValue) =>
      set(multiLevelPortfolioStrategyFilterAtom(id), newValue),
});

export const geoLevelFilterType = atomFamily<GeoLevelFilterType | undefined, BlockId>({
  key: 'geoLevelFilterType',
  default: undefined,
});

export const sectorLevelFilterType = atomFamily<SectorLevelFilterType | undefined, BlockId>({
  key: 'sectorLevelFilterType',
  default: undefined,
});

export const viewCoverPageImage = atom<string>({
  key: 'viewCoverPageImage',
  default: DEFAULT_STOCK_PHOTO,
  effects: [resetOnStudioReset],
});
export const sectionPageBackgroundImage = atom<string>({
  key: 'sectionPageBackgroundImage',
  default: DEFAULT_STOCK_PHOTO,
  effects: [resetOnStudioReset],
});

export const viewImages = atom<ImageDetails[] | undefined>({
  key: 'viewImages',
  default: undefined,
  effects: [resetOnStudioReset],
});
export const sectionPageBackgroundImages = atom<ImageDetails[] | undefined>({
  key: 'sectionPageBackgroundImages',
  default: undefined,
  effects: [resetOnStudioReset],
});

export const viewRichText = atomFamily<EditorValue | undefined, BlockId>({
  key: 'viewRichText',
  default: undefined,
});

export const viewShowVenncast = atomFamily<boolean | undefined, BlockId>({
  key: 'viewShowVenncast',
  default: undefined,
});

export const viewImageId = atomFamily<string | undefined, BlockId>({
  key: 'viewImageId',
  default: undefined,
});

export const viewPrimaryColor = atom<string>({
  key: 'viewPrimaryColor',
  default: '#1c5af3',
  effects: [resetOnStudioReset],
});

export const viewSectionTextColor = atom<string>({
  key: 'viewSectionTextColor',
  default: '#ffffff',
  effects: [resetOnStudioReset],
});

export const viewTaxRatesPerSubject = atom<Record<string, TaxRate> | undefined>({
  key: 'viewTaxRatesPerSubject',
  default: undefined,
});

export const blockHoldingsCategoryIds = atomFamily<HoldingsCategoryId[] | undefined, BlockId>({
  key: 'blockHoldingsCategoryIds',
  default: undefined,
});

export const blockYAxisUnitFormat = atomFamily<UnitFormat, BlockId>({
  key: 'blockYAxisUnitFormat',
  default: UnitFormat.PERCENT,
});

// Meant to be accessed via selector `hideBlockBorders` to ensure it's being used on the right blocks
const hideBlockBordersSetting = atomFamily<boolean, BlockId>({
  key: 'hideBlockBordersSetting',
  default: true,
});

export const canToggleHideBlockBorders = selectorFamily<boolean, BlockId>({
  key: 'canToggleHideBlockBorders',
  get:
    (id) =>
    ({ get }) => {
      const settingId = get(blockSettingId(id));
      const blockSettingsData = get(blockSettingsMap)[settingId ?? ''];
      const blockType = blockSettingsData?.customBlockType;
      return (blockType === 'MARKDOWN' || blockType === 'IMAGE') && get(isReportState);
    },
});

export const hideBlockBorders = selectorFamily<boolean, BlockId>({
  key: 'hideBlockBorders',
  get:
    (id) =>
    ({ get }) =>
      get(canToggleHideBlockBorders(id)) ? get(hideBlockBordersSetting(id)) : false,
  set:
    (id) =>
    ({ set }, hideBorders) =>
      set(hideBlockBordersSetting(id), hideBorders),
});

/** true iff the current reports layout has been changed since the view has been opened */
export const hasChangedLayoutState = atom<boolean>({
  key: 'hasChangedLayoutState',
  default: false,
  effects: [resetOnStudioReset],
});

export const filterHoldingsToLevel = (
  holdings: HoldingsBenchmarkCategory[],
  level: number,
): HoldingsBenchmarkCategory[] => {
  if (level <= 0) {
    return [];
  }

  return holdings.map((holding) => ({
    ...holding,
    children: filterHoldingsToLevel(holding.children, level - 1),
  }));
};
export const allHoldingsCategories = selectorFamily<HoldingsBenchmarkCategory[], BlockId>({
  key: 'allHoldingsCategories',
  get:
    (id) =>
    ({ get }) => {
      const settings = get(blockSettings(id));
      return get(holdingsCategoriesTree(convertHoldingsBlockTypeToBreakdown(settings.customBlockType)));
    },
});

export const holdingsCategories = selectorFamily<HoldingsBenchmarkCategory[], BlockId>({
  key: 'holdingsCategories',
  get:
    (id) =>
    ({ get }) => {
      const allCategories = get(allHoldingsCategories(id));
      const blockType = get(blockSettings(id)).customBlockType;
      const selectedCategoryIds = get(blockHoldingsCategoryIds(id))?.map((id) => id.id);
      if (blockType === 'GEOGRAPHY_EXPOSURE') {
        return allCategories;
      }
      const getCategories = (allCategories: HoldingsBenchmarkCategory[]) => {
        if (selectedCategoryIds) {
          const settings = get(blockSettings(id));
          const isTree = settings.customBlockType === 'SECTOR_EXPOSURE';
          if (isTree) {
            return allCategories.map((category) => ({
              ...category,
              children: category.children.filter((child) => selectedCategoryIds.includes(child.id.id)),
            }));
          }
          return allCategories.filter((category) => selectedCategoryIds.includes(category.id.id));
        }
        return allCategories;
      };

      return getCategories(allCategories);
    },
  set:
    (id) =>
    ({ get, set }, newValue) => {
      // no idea what we do with DefaultValue
      if (newValue instanceof DefaultValue) {
        // TODO: we should propagate reset and call reset on ALL blockHoldingsCategoryIds atoms
        return;
      }

      // select all
      const allCategories = get(allHoldingsCategories(id));
      const settings = get(blockSettings(id));

      const allCategoriesToCheck = isTreeBlock(settings.customBlockType)
        ? allCategories.flatMap((category) => category.children).map((category) => category.id)
        : allCategories.map((category) => category.id);

      const selectedIds = newValue.map((category) => category.id);
      const selectedIdStrings = selectedIds.map((id) => id.id);
      if (allCategoriesToCheck.every((id) => selectedIdStrings.includes(id.id))) {
        // undefined means all categories selected
        set(blockHoldingsCategoryIds(id), undefined);
        return;
      }

      // if not select all then either filter to set of IDs or an empty array for select none
      set(blockHoldingsCategoryIds(id), selectedIds);
    },
});

export const showBlockChangesToggle = atomFamily<boolean, BlockId>({
  key: 'showBlockChangesToggle',
  default: false,
});

export const showBlockChangesColumn = selectorFamily<boolean, BlockId>({
  key: 'showBlockChangesColumn',
  get:
    (id) =>
    ({ get }) => {
      const showBlockChangesEnabled = get(showBlockChangesToggle(id));
      const subjects = get(blockLimitedHoldingsSubjectsWithFees(id));

      return showBlockChangesEnabled && subjects.length === 2;
    },
  set:
    (id) =>
    ({ set }, newValue) => {
      set(showBlockChangesToggle(id), newValue);
    },
});

export const showBlockGainLoss = atomFamily<boolean, BlockId>({
  key: 'showBlockGainLoss',
  default: false,
});

export const showBlockNetTaxImpact = atomFamily<boolean, BlockId>({
  key: 'showBlockNetTaxImpact',
  default: false,
});
